import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, openProposalPdf } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  FileSignature, Plus, Search, Download, Lock, LayoutDashboard, List,
  Trophy, CheckCircle2, Send, Clock, FilePlus2, AlertCircle, MessageSquare, Copy,
} from 'lucide-react';

const STATUS_COLOR = {
  Draft: 'bg-slate-100 text-slate-700',
  'Pending approval': 'bg-amber-100 text-amber-700',
  Generated: 'bg-blue-100 text-blue-700',
  Sent: 'bg-indigo-100 text-indigo-700',
  Accepted: 'bg-emerald-100 text-emerald-700',
  Won: 'bg-emerald-100 text-emerald-700',
  Lost: 'bg-rose-100 text-rose-700',
  Cancelled: 'bg-slate-100 text-slate-500',
  Superseded: 'bg-slate-100 text-slate-500',
};

function fmtINR(n) {
  if (n == null) return '—';
  try { return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`; }
  catch { return `₹${n}`; }
}

// ─── Register (list) tab ──────────────────────────────────────────────────
function RegisterTab({ rows, canSeeMoney, canApprove, pendingCount, approvalEnabled }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [readyToSend, setReadyToSend] = useState(false);
  const [awaitingApproval, setAwaitingApproval] = useState(false);
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [expanded, setExpanded] = useState({}); // base_number → bool

  // ─── Collapse revisions by base_number. The visible row is the latest
  //     revision; older ones hide behind a "R6 · 5 revisions" chip the
  //     user can expand. "Needs approval" tags and other open-action
  //     chips never show on a Superseded row.
  const groups = useMemo(() => {
    const by = new Map();
    for (const r of rows) {
      const base = r.base_number || (r.number || '').replace(/-R\d+$/, '');
      if (!by.has(base)) by.set(base, []);
      by.get(base).push(r);
    }
    const out = [];
    for (const [base, list] of by.entries()) {
      list.sort((a, b) => (b.revision || 1) - (a.revision || 1)); // latest first
      const head = list[0];
      const olders = list.slice(1);
      out.push({ base, head, olders });
    }
    return out;
  }, [rows]);

  const filtered = useMemo(() => groups.filter(({ head }) => {
    const r = head;
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (readyToSend) {
      if (r.status !== 'Generated') return false;
      if (!(r.to_emails && r.to_emails.length > 0)) return false;
    }
    if (awaitingApproval && r.status !== 'Pending approval') return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.number || '').toLowerCase().includes(s)
        || (r.client_name || '').toLowerCase().includes(s)
        || (r.prepared_by_name || r.created_by_name || '').toLowerCase().includes(s);
  }), [groups, search, statusFilter, readyToSend, awaitingApproval]);

  return (
    <Card className="shine-card p-4 rounded-xl" data-testid="proposals-register-tab">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-4 w-4 text-ink-2 absolute left-2 top-2.5" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search by number, client, salesperson"
                 className="pl-8" data-testid="proposals-search" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); if (v !== 'all') setReadyToSend(false); }}>
          <SelectTrigger className="w-[180px]" data-testid="proposals-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Draft">Draft</SelectItem>
            {approvalEnabled && <SelectItem value="Pending approval">Pending approval</SelectItem>}
            <SelectItem value="Generated">Generated</SelectItem>
            <SelectItem value="Sent">Sent</SelectItem>
            <SelectItem value="Accepted">Accepted</SelectItem>
            <SelectItem value="Won">Won</SelectItem>
            <SelectItem value="Lost">Lost</SelectItem>
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={() => { setReadyToSend((v) => !v); if (!readyToSend) { setStatusFilter('all'); setAwaitingApproval(false); } }}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition border ${
            readyToSend
              ? 'bg-ink text-white border-ink'
              : 'bg-white text-ink-2 hover:text-ink border-ink/15'
          }`}
          data-testid="proposals-ready-to-send-filter"
        >
          Ready to send
        </button>
        <button
          type="button"
          onClick={() => setShowSuperseded((v) => !v)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition border ${
            showSuperseded
              ? 'bg-ink text-white border-ink'
              : 'bg-white text-ink-2 hover:text-ink border-ink/15'
          }`}
          data-testid="proposals-show-superseded">
          Show superseded
        </button>
        {canApprove && approvalEnabled && (
          <button
            type="button"
            onClick={() => { setAwaitingApproval((v) => !v); if (!awaitingApproval) { setStatusFilter('all'); setReadyToSend(false); } }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition border inline-flex items-center gap-1 ${
              awaitingApproval
                ? 'bg-amber-600 text-white border-amber-600'
                : 'bg-white text-ink-2 hover:text-ink border-ink/15'
            }`}
            data-testid="proposals-awaiting-approval-filter"
          >
            Awaiting my approval
            {pendingCount > 0 && (
              <span className={`ml-0.5 rounded-full text-[10px] font-bold px-1.5 py-0.5 ${awaitingApproval ? 'bg-white text-amber-700' : 'bg-amber-600 text-white'}`}>
                {pendingCount}
              </span>
            )}
          </button>
        )}
        <div className="ml-auto text-xs text-ink-2">{filtered.length} of {groups.length}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-ink-2 text-xs uppercase border-b border-ink/10">
              <th className="py-2 pr-3">Number</th>
              <th className="py-2 pr-3">Client</th>
              <th className="py-2 pr-3">Services</th>
              <th className="py-2 pr-3 text-right">Amount</th>
              <th className="py-2 pr-3">Prepared by</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Date</th>
              <th className="py-2 pr-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.flatMap(({ base, head, olders }) => {
              const r = head;
              const isSuperseded = r.status === 'Superseded';
              const isOpen = !!expanded[base];
              const rows = [
                <tr key={r.id} className="border-b border-ink/5 hover:bg-page/50">
                  <td className="py-2 pr-3 font-mono text-xs">
                    <Link to={`/proposals/${r.id}`} className="text-ink hover:underline" data-testid={`proposal-number-${r.id}`}>
                      {r.number}
                    </Link>
                    {olders.length > 0 && (
                      <button type="button"
                              onClick={() => setExpanded((x) => ({ ...x, [base]: !x[base] }))}
                              className="ml-1 inline-flex items-center rounded-full border border-ink/15 px-1.5 py-0.5 text-[10px] text-ink-2 hover:bg-page"
                              title={`R${r.revision} is the latest · ${olders.length} older revision(s)`}
                              data-testid={`proposal-revision-chip-${base}`}>
                        R{r.revision} · {olders.length + 1} revision{olders.length ? 's' : ''}
                      </button>
                    )}
                  </td>
                  <td className="py-2 pr-3 font-medium text-ink">{r.client_name}</td>
                  <td className="py-2 pr-3">
                    <div className="flex flex-wrap gap-1">
                      {(r.services || []).map((s) => (
                        <Badge key={s} variant="outline" className="text-[10px]">{s.replace('_', ' ')}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {r.money_hidden ?
                      <span className="inline-flex items-center gap-1 text-ink-2" title="You can only see amounts for proposals you prepared or when the owner enables 'See money' for your role."><Lock className="h-3 w-3" />—</span> :
                      fmtINR(r.total)}
                  </td>
                  <td className="py-2 pr-3 text-ink-2 text-xs">{r.prepared_by_name || r.created_by_name}</td>
                  <td className="py-2 pr-3">
                    <Badge className={`${STATUS_COLOR[r.status] || 'bg-slate-100 text-slate-700'} border-0`}>{r.status}</Badge>
                    {/* Open-action chips are hidden on Superseded rows. */}
                    {!isSuperseded && approvalEnabled && r.needs_approval && <Badge variant="outline" className="ml-1 text-[10px] text-amber-700 border-amber-300">Needs approval</Badge>}
                  </td>
                  <td className="py-2 pr-3 text-ink-2 text-xs">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    <Button size="sm" variant="outline"
                            onClick={() => openProposalPdf(r.id, {
                              filename: `${r.number || 'proposal'}.pdf`,
                              onError: (m) => toast.error(m),
                            })}
                            data-testid={`proposal-pdf-${r.id}`}>
                      <Download className="h-3 w-3 mr-1" /> PDF
                    </Button>
                  </td>
                </tr>,
              ];
              if (isOpen) {
                olders
                  .filter((o) => showSuperseded || o.status !== 'Superseded')
                  .forEach((o) => rows.push(
                    <tr key={o.id} className="border-b border-ink/5 bg-page/30 text-xs">
                      <td className="py-1.5 pl-6 pr-3 font-mono text-ink-2">
                        <Link to={`/proposals/${o.id}`} className="hover:underline" data-testid={`proposal-number-${o.id}`}>
                          {o.number}
                        </Link>
                      </td>
                      <td colSpan={2} className="py-1.5 pr-3 text-ink-2">older revision</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-ink-2">
                        {o.money_hidden ? '—' : fmtINR(o.total)}
                      </td>
                      <td className="py-1.5 pr-3 text-ink-2">{o.prepared_by_name || o.created_by_name}</td>
                      <td className="py-1.5 pr-3">
                        <Badge className={`${STATUS_COLOR[o.status] || 'bg-slate-100 text-slate-700'} border-0`}>{o.status}</Badge>
                      </td>
                      <td className="py-1.5 pr-3 text-ink-2">{o.created_at ? new Date(o.created_at).toLocaleDateString() : '—'}</td>
                      <td className="py-1.5 pr-3 text-right"></td>
                    </tr>
                  ));
              }
              return rows;
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="py-8 text-center text-ink-2">
                {readyToSend
                  ? 'No proposals are ready to send yet. Generate a proposal and add recipients to it.'
                  : (rows.length === 0
                      ? <>No proposals yet. Click <b>+ New proposal</b> to create the first one.</>
                      : 'No proposals match your filters.')}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── Dashboard tab ────────────────────────────────────────────────────────
function Tile({ icon: Icon, label, value, tint = 'ink' }) {
  const tintBg = {
    ink: 'text-ink',
    green: 'text-green-ink',
    blue: 'text-blue-ink',
    amber: 'text-amber-700',
    rose: 'text-rose-600',
  }[tint] || 'text-ink';
  return (
    <Card className="shine-card p-4 rounded-xl">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase text-ink-2">{label}</div>
        <Icon className={`h-4 w-4 ${tintBg}`} />
      </div>
      <div className={`text-2xl font-bold mt-1 ${tintBg}`}>{value}</div>
    </Card>
  );
}

function DashboardTab({ canSeeMoney }) {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/proposals/dashboard?period=${period}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load dashboard'));
  }, [period]);

  const t = data?.tiles || {};
  const money = !data?.money_hidden && canSeeMoney;

  return (
    <div className="space-y-4" data-testid="proposals-dashboard-tab">
      <div className="flex items-center justify-between">
        <div className="text-xs text-ink-2">Period:</div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[150px]" data-testid="dashboard-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">Last 7 days</SelectItem>
            <SelectItem value="month">Last 30 days</SelectItem>
            <SelectItem value="quarter">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <Tile icon={FilePlus2} label="Created" value={t.created ?? 0} />
        {t.pending_approval > 0 && <Tile icon={Clock} label="Pending approval" value={t.pending_approval} tint="amber" />}
        <Tile icon={CheckCircle2} label="Ready to send" value={t.ready_to_send ?? 0} tint="blue" />
        <Tile icon={Send} label="Sent" value={t.sent ?? 0} tint="blue" />
        <Tile icon={CheckCircle2} label="Accepted" value={t.accepted ?? 0} tint="green" />
        <Tile icon={Trophy} label="Won" value={t.won ?? 0} tint="green" />
        <Tile icon={MessageSquare} label="Changes requested" value={t.changes_requested ?? 0} tint="rose" />
        {money
          ? <Tile icon={Trophy} label="Value won (₹)" value={fmtINR(t.value_won ?? 0)} tint="green" />
          : <Tile icon={Lock} label="Value won (₹)" value="Restricted" tint="ink" />}
      </div>

      <Card className="shine-card p-4 rounded-xl">
        <h3 className="text-sm font-semibold text-ink mb-2">By salesperson</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-ink-2 uppercase border-b border-ink/10">
                <th className="py-2 pr-2">Salesperson</th>
                <th className="py-2 pr-2 text-right">Created</th>
                <th className="py-2 pr-2 text-right">Sent</th>
                <th className="py-2 pr-2 text-right">Won</th>
                <th className="py-2 pr-2 text-right">Win %</th>
                {money && <th className="py-2 pr-2 text-right">Value sent (₹)</th>}
              </tr>
            </thead>
            <tbody>
              {(data?.by_user || []).map((u) => (
                <tr key={u.user_id} className="border-b border-ink/5">
                  <td className="py-2 pr-2 font-medium text-ink">{u.name || '—'}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{u.created}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{u.sent}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{u.won}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">
                    {u.sent > 0 ? `${Math.round(100 * u.won / u.sent)}%` : '—'}
                  </td>
                  {money && (
                    <td className="py-2 pr-2 text-right tabular-nums">{fmtINR(u.value || 0)}</td>
                  )}
                </tr>
              ))}
              {(!data?.by_user || data.by_user.length === 0) && (
                <tr><td colSpan={money ? 6 : 5} className="py-6 text-center text-ink-2">
                  No activity in this period.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────
export default function ProposalsPage() {
  const { hasPerm, isOwner } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState('register');
  const [approvalEnabled, setApprovalEnabled] = useState(false);
  const canSeeMoney = hasPerm('see_money');
  const canCreate = hasPerm('proposals_create');
  const canApprove = hasPerm('proposals_approve');
  const pendingCount = useMemo(() => rows.filter((r) => r.status === 'Pending approval').length, [rows]);

  const load = async () => {
    try {
      const { data } = await api.get('/proposals');
      setRows(data);
    } catch {
      toast.error('Could not load proposals');
    }
  };
  useEffect(() => {
    load();
    api.get('/settings/proposals-workflow')
      .then((r) => setApprovalEnabled(!!r.data?.require_approval))
      .catch(() => {});
  }, []);

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="proposals-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink flex items-center gap-2">
            <FileSignature className="h-6 w-6 text-green-ink" /> Proposals
          </h1>
          <p className="text-sm text-ink-2 mt-1">Quotations, revisions and dashboards.</p>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2">
            {isOwner && (
              <>
                <Button variant="outline"
                        onClick={() => navigate('/proposals/duplicates')}
                        data-testid="duplicates-link-btn">
                  <Copy className="h-4 w-4 mr-1" /> Duplicate revisions
                </Button>
                <Button variant="outline"
                        onClick={() => navigate('/proposals/price-changes')}
                        data-testid="price-changes-link-btn">
                  Price change report
                </Button>
                <Button variant="outline"
                        onClick={() => navigate('/proposals/leads-dry-run')}
                        data-testid="leads-dry-run-link-btn">
                  Leads dry-run
                </Button>
              </>
            )}
            <Button onClick={() => navigate('/proposals/new')} data-testid="new-proposal-btn">
              <Plus className="h-4 w-4 mr-1" /> New proposal
            </Button>
          </div>
        )}
      </div>

      <div className="inline-flex items-center gap-1 rounded-full bg-slate-100 p-1">
        {['register', 'dashboard'].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-state={tab === t ? 'active' : 'inactive'}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
              tab === t ? 'bg-ink text-white' : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid={`proposals-tab-${t}`}
          >
            {t === 'register' ? (<><List className="h-3.5 w-3.5 inline mr-1" /> Register</>) : (<><LayoutDashboard className="h-3.5 w-3.5 inline mr-1" /> Dashboard</>)}
          </button>
        ))}
      </div>

      {tab === 'register'
        ? <RegisterTab rows={rows} canSeeMoney={canSeeMoney} canApprove={canApprove} pendingCount={pendingCount} approvalEnabled={approvalEnabled} />
        : <DashboardTab canSeeMoney={canSeeMoney} />}
    </div>
  );
}
